import type { VocabItem } from '../../types/exercise';

/**
 * Generate distractor options for multiple choice exercises
 * Selects plausible wrong answers from the same vocabulary pool
 */
export function generateDistractors(
  correctItem: VocabItem,
  vocabPool: VocabItem[],
  count: number = 3,
  useTarget: boolean = true
): string[] {
  const correctAnswer = useTarget ? correctItem.target : correctItem.primary;

  // Filter out the correct answer and get candidates
  const candidates = vocabPool
    .filter(item => item.id !== correctItem.id)
    .map(item => useTarget ? item.target : item.primary)
    .filter(text => text !== correctAnswer);

  // Prioritize items from the same level if available
  const sameLevelCandidates = vocabPool
    .filter(item =>
      item.id !== correctItem.id &&
      item.level === correctItem.level
    )
    .map(item => useTarget ? item.target : item.primary)
    .filter(text => text !== correctAnswer);

  // Prioritize items with similar length
  const correctLength = correctAnswer.length;
  const sortedByLengthSimilarity = [...candidates].sort((a, b) => {
    const diffA = Math.abs(a.length - correctLength);
    const diffB = Math.abs(b.length - correctLength);
    return diffA - diffB;
  });

  // Combine strategies: prefer same level, then similar length
  const prioritized = [
    ...new Set([...sameLevelCandidates, ...sortedByLengthSimilarity])
  ];

  // Shuffle and select
  const shuffled = shuffleArray(prioritized);
  return shuffled.slice(0, count);
}

/**
 * Generate multiple choice options including the correct answer
 * Returns array of 4 options with the correct answer at a random position
 */
export function generateMultipleChoiceOptions(
  correctItem: VocabItem,
  vocabPool: VocabItem[],
  useTarget: boolean = true
): { options: string[]; correctIndex: number } {
  const correctAnswer = useTarget ? correctItem.target : correctItem.primary;
  const distractors = generateDistractors(correctItem, vocabPool, 3, useTarget);

  // If we don't have enough distractors, pad with generic options
  while (distractors.length < 3) {
    const placeholder = generatePlaceholderDistractor(correctAnswer, distractors.length);
    if (!distractors.includes(placeholder) && placeholder !== correctAnswer) {
      distractors.push(placeholder);
    }
  }

  // Combine correct answer with distractors
  const allOptions = [correctAnswer, ...distractors.slice(0, 3)];

  // Shuffle to randomize correct answer position
  const shuffledOptions = shuffleArray(allOptions);
  const correctIndex = shuffledOptions.indexOf(correctAnswer);

  return {
    options: shuffledOptions,
    correctIndex,
  };
}

/**
 * Generate a placeholder distractor when pool is too small
 */
function generatePlaceholderDistractor(correctAnswer: string, index: number): string {
  // Create slightly modified versions of the correct answer
  const modifications = [
    correctAnswer + 's',
    correctAnswer.slice(0, -1),
    correctAnswer.charAt(0).toUpperCase() + correctAnswer.slice(1),
    '...',
  ];

  return modifications[index % modifications.length];
}

/**
 * Fisher-Yates shuffle algorithm
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Generate pairs for MATCH_PAIRS exercise
 * Creates matching left/right columns with shuffled right side
 */
export function generateMatchPairs(
  items: VocabItem[],
  count: number = 4,
  direction: 'primary-to-target' | 'target-to-primary' = 'primary-to-target'
): {
  leftItems: { id: string; text: string }[];
  rightItems: { id: string; text: string }[];
  correctPairs: Record<string, string>;
} {
  // Select items for matching
  const selectedItems = shuffleArray(items).slice(0, Math.min(count, items.length));

  const leftItems = selectedItems.map((item, index) => ({
    id: `left-${index}`,
    text: direction === 'primary-to-target' ? item.primary : item.target,
  }));

  const rightItemsUnshuffled = selectedItems.map((item, index) => ({
    id: `right-${index}`,
    text: direction === 'primary-to-target' ? item.target : item.primary,
  }));

  // Shuffle right items for display
  const rightItems = shuffleArray(rightItemsUnshuffled);

  // Create correct pairs mapping
  const correctPairs: Record<string, string> = {};
  selectedItems.forEach((_, index) => {
    correctPairs[`left-${index}`] = `right-${index}`;
  });

  return {
    leftItems,
    rightItems,
    correctPairs,
  };
}

/**
 * Shuffle letters of a word for WORD_BUILD exercise
 */
export function shuffleLetters(word: string): string[] {
  const letters = word.split('');
  let shuffled = shuffleArray(letters);

  // Make sure shuffled version is different from original
  let attempts = 0;
  while (shuffled.join('') === word && attempts < 10) {
    shuffled = shuffleArray(letters);
    attempts++;
  }

  return shuffled;
}

/**
 * Generate a cloze sentence from examples or create a simple one
 */
export function generateClozeSentence(
  item: VocabItem,
  direction: 'primary-to-target' | 'target-to-primary'
): {
  sentenceWithGap: string;
  gapWord: string;
  gapPosition: { start: number; end: number };
} | null {
  const targetWord = direction === 'primary-to-target' ? item.target : item.primary;

  // Try to use existing examples
  if (item.examples && item.examples.length > 0) {
    for (const example of item.examples) {
      const lowerExample = example.toLowerCase();
      const lowerTarget = targetWord.toLowerCase();
      const index = lowerExample.indexOf(lowerTarget);

      if (index !== -1) {
        const sentenceWithGap =
          example.slice(0, index) +
          '_____' +
          example.slice(index + targetWord.length);

        return {
          sentenceWithGap,
          gapWord: targetWord,
          gapPosition: { start: index, end: index + 5 }, // 5 = length of "_____"
        };
      }
    }
  }

  // If no suitable example found, return null (caller should handle)
  return null;
}
